import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const EntitySelectDialog = ({ data, closeDialog }) => {
  const rsrc = useRsrc();
  const entityListRef = useRef();
  const [entityDef, setEntityDef] = useState();

  // For single select
  const [selectedEntities, setSelectedEntities] = useState([]);

  // For multiple select
  const initialHeaderChecked = data.headerChecked ?? true;
  const [headerChecked, setHeaderChecked] = useState(initialHeaderChecked); // true if the checkbox at the top of the grid is selected indicating that all filtered rows are to be counted
  const headerCheckedRef = useRef(initialHeaderChecked); // AG Grid callbacks are created once, so they read a ref
  const pendingIdsRef = useRef(new Set(data.selectedIds ?? [])); // ids to re-select as their blocks load

  const multiple = data.multiple;

  const onHeaderCheckmarkToggle = (checked, api) => {
    headerCheckedRef.current = checked;
    setHeaderChecked(checked);
    if (checked) pendingIdsRef.current.clear();
    api.deselectAll();
    api.redrawRows(); // re-run the callbacks above for rows already rendered
  };

  // Infinite model: a row can only be selected once its block has loaded
  const restoreSelectedRows = (api) => {
    if (!pendingIdsRef.current.size) return;
    api.forEachNode((node) => {
      if (node.data && pendingIdsRef.current.delete(node.data.id)) node.setSelected(true);
    });
  };

  // Lifecycle

  useEffect(() => {
    rsrc.entityDef(data.module, data.entity).then((entityDef) => {
      setEntityDef(entityDef);
    });
  }, []);

  const augFieldDefs = (fieldToFieldDef) => {
    Object.values(fieldToFieldDef).forEach((fieldDef) => {
      fieldDef.disableHyperlink = true;
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;
    if (multiple) {
      fieldToFieldDef.checkbox = {
        ...GridConstants.checkbox,
        headerCheckboxSelection: false,
        headerComponent: HeaderCheckbox,
        headerComponentParams: { onHeaderCheckmarkToggle, initialHeaderChecked },
        checkboxSelection: () => !headerCheckedRef.current,
      };
    }

    GridConstants.removeAuditFieldDefs(fieldToFieldDef);
  };

  const augRowsReq = useCallback((rowsReq) => {
    if (data.expr || data.parentFilterEntity) {
      rowsReq.expr = ExprUtils.and(
        rowsReq.expr,
        data.expr,
        data.parentFilterEntity ?
          ExprUtils.parentFilterExpr(data.parentFilterFkField, data.parentFilterEntity, data.parentFilterEntityModule)
        : null,
      );
    }
    rowsReq.query = data.query;
    rowsReq.replaceParams = data.replaceParams;
  }, []);

  const submit = () => {
    if (!multiple) {
      closeDialog({ selectedEntity: selectedEntities[0] });
      return;
    }

    let expr;
    let selectedCount;
    let selectedIds = [];
    if (headerChecked) {
      const api = entityListRef.current.gridRef.current.gridApiRef.current;
      const fieldDefs = entityListRef.current.fieldDefs;
      expr = ExprUtils.filterModelToExpr(api.getFilterModel(), fieldDefs);
      selectedCount = entityListRef.current.gridRef.current.getRowCount();
    } else {
      selectedIds = [...selectedEntities.map((entity) => entity.id), ...pendingIdsRef.current];
      expr = ExprUtils.setExpr('id', selectedIds);
      selectedCount = selectedIds.length;
    }

    closeDialog({ expr, selectedCount, headerChecked, selectedIds });
  };

  return (
    entityDef && (
      <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="lg">
        <DialogTitle>{'Select ' + StringUtils.toSingular(entityDef.displayName)}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: '60vh' }}>
            <EntityList
              ref={entityListRef}
              id={data.field}
              module={data.module}
              entity={data.entity}
              augFieldDefs={augFieldDefs}
              augRowsReq={augRowsReq}
              gridOptions={{
                rowSelection: multiple ? 'multiple' : 'single',
                suppressRowClickSelection: multiple,
                getRowId: (params) => String(params.data.id), // infinite model persists selection by row id across filter/sort
                getRowClass: multiple ? () => (headerCheckedRef.current ? 'ag-row-selected' : undefined) : undefined, // AG Grid's own highlight class
              }}
              events={{
                onSelectionChanged: (e) => setSelectedEntities(e.api.getSelectedRows()),
                onModelUpdated: (e) => restoreSelectedRows(e.api),
              }}
              showTitle={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={!headerChecked && selectedEntities.length === 0}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

// AG Grid's headerCheckboxSelection is not supported by the infinite row model, so select-all is done over loaded rows
const HeaderCheckbox = ({ api, initialHeaderChecked, onHeaderCheckmarkToggle }) => {
  const [checked, setChecked] = useState(initialHeaderChecked);

  const onChange = (event) => {
    onHeaderCheckmarkToggle(event.target.checked, api);
    setChecked(event.target.checked);
  };

  return (
    <div className={'ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper' + (checked ? ' ag-checked' : '')}>
      <input type="checkbox" className="ag-input-field-input ag-checkbox-input" checked={checked} onChange={onChange} />
    </div>
  );
};
